import { memo, useCallback, useMemo, useState } from 'react';
import { Box, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Typography, useTheme } from '@mui/material';
import CheckIcon from '@mui/icons-material/CheckBox';
import FailIcon from '@mui/icons-material/DisabledByDefaultRounded';
import {
    highlightSpecialWords,
    useAgentResponse,
    useLogTicketProcess,
    useSelectedComment,
    useSelectedCommentDecisionQuery,
    useSetAlert,
    useTicketFinalizeSubmit,
    useTicketNav,
} from '@tymely/services';
import { BaseModal, LinkPreview } from '@tymely/components';
import { IActionExecution, isLastTicketSubmittedAtom } from '@tymely/atoms';
import { useApi } from '@tymely/api';
import { useSetAtom } from 'jotai';

export const SubmitModal = memo((props: { open: boolean; onClose: () => void }) => {
    const api = useApi();
    const theme = useTheme();
    const [linkEl, setLinkEl] = useState<Element | null>(null);
    const ticketFinalizeSubmit = useTicketFinalizeSubmit();
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [actionChecks, setActionChecks] = useState<Record<string, boolean>>({});
    const { logFailed, logProcessed } = useLogTicketProcess();
    const { agentResponse } = useAgentResponse();
    const [error, setError] = useState<string>();
    const setAlert = useSetAlert();
    const selectedComment = useSelectedComment();
    const { data: decision } = useSelectedCommentDecisionQuery();
    const setIsLastTicketSubmitted = useSetAtom(isLastTicketSubmittedAtom);
    const withActionChecks = decision?.status === 'BETA';
    const actions = decision?.action_executions || [];
    const { handleNext } = useTicketNav();

    const onSubmit = useCallback(() => {
        setSubmitting(true);
        setIsLastTicketSubmitted(false);

        const timeoutId = setTimeout(() => {
            setIsLastTicketSubmitted(true);
        }, 30000);

        const promise = ticketFinalizeSubmit(false, !withActionChecks)
            .then(() => setAlert('Ticket has been submitted', 'success'))
            .catch((result) => {
                setAlert(result.message, 'error');
            })
            .finally(() => {
                clearTimeout(timeoutId);
                if (withActionChecks) {
                    setSubmitted(true);
                }
                setSubmitting(false);
                setIsLastTicketSubmitted(true);
            });

        if (!withActionChecks) {
            logProcessed('submitted');
            handleNext(promise);
        }
    }, [ticketFinalizeSubmit, withActionChecks, handleNext]);

    const onCheck = useCallback(
        (action: IActionExecution, checked: boolean) => {
            setActionChecks((checks) => ({ ...checks, [action.id]: checked }));
        },
        [actionChecks],
    );

    const canProceed = useMemo(() => {
        return Object.keys(actionChecks).length === actions.length;
    }, [actionChecks, actions]);

    const onCheckActions = useCallback(() => {
        const failedActions = actions.filter((action) => actionChecks[action.id] === false);
        const promise = Promise.resolve() as Promise<any>;
        if (failedActions.length) {
            setSubmitting(true);
            logFailed(
                decision!.workflow_id,
                failedActions.map((action) => action.action_metadata.title),
            );
        } else {
            logProcessed('submitted');
        }
        promise
            .then(() => {
                const waitFor = selectedComment
                    ? new Promise((resolve) => {
                          api.post(`comment/${selectedComment.id}/post_process`);
                          setTimeout(resolve, 100);
                      })
                    : undefined;
                return handleNext(waitFor);
            })
            .catch(() => {
                setError('Failed to update workflow status');
            })
            .finally(() => {
                setSubmitting(false);
            });
    }, [decision, actionChecks, handleNext]);

    const previewHtml = agentResponse ? highlightSpecialWords(agentResponse) : undefined;

    return (
        <BaseModal
            title={submitted ? 'Verify actions' : 'Submit response'}
            okLabel={submitted ? 'Go to next ticket' : 'Submit'}
            maxWidth="md"
            fullWidth
            loading={submitting}
            closable={!submitted}
            disabled={submitted ? !canProceed : false}
            open={props.open}
            onOk={submitted ? onCheckActions : onSubmit}
            onClose={props.onClose}
            scroll="paper"
        >
            {submitted ? (
                <Box display="flex" flexDirection="column">
                    <List sx={{ width: 'fit-content', margin: '0 auto' }}>
                        {actions.map((action) => (
                            <ListItemButton disableRipple key={action.id}>
                                <ListItemIcon
                                    sx={{
                                        minWidth: theme.spacing(3),
                                        alignSelf: 'flex-start',
                                    }}
                                    onClick={() => onCheck(action, true)}
                                >
                                    <IconButton
                                        sx={{
                                            color: actionChecks[action.id] === true ? 'success.main' : 'disabled',
                                        }}
                                    >
                                        <CheckIcon />
                                    </IconButton>
                                </ListItemIcon>
                                <ListItemIcon
                                    sx={{
                                        minWidth: theme.spacing(6),
                                        alignSelf: 'flex-start',
                                    }}
                                    onClick={() => onCheck(action, false)}
                                >
                                    <IconButton
                                        sx={{
                                            color: actionChecks[action.id] === false ? 'error.main' : 'disabled',
                                        }}
                                    >
                                        <FailIcon />
                                    </IconButton>
                                </ListItemIcon>
                                <ListItemText
                                    primary={action.action_metadata.title}
                                    secondary={action.action_metadata.description}
                                />
                            </ListItemButton>
                        ))}
                    </List>
                    {error && (
                        <Typography variant="subtitle1" sx={{ color: 'error.main', marginTop: 'auto' }}>
                            {error}
                        </Typography>
                    )}
                </Box>
            ) : (
                <>
                    {linkEl && <LinkPreview anchorEl={linkEl} onClose={() => setLinkEl(null)} />}
                    {previewHtml && (
                        <Typography
                            paragraph
                            sx={{
                                whiteSpace: 'pre-line',
                                '& table': {
                                    borderCollapse: 'collapse',
                                },
                            }}
                            dangerouslySetInnerHTML={{
                                __html: previewHtml,
                            }}
                            onMouseOver={(event) => {
                                setLinkEl(event.target as Element);
                            }}
                            onClick={(event) => {
                                if (event.target instanceof HTMLAnchorElement) {
                                    window.open(event.target.href, '_blank');
                                    event.preventDefault();
                                }
                            }}
                        ></Typography>
                    )}
                </>
            )}
        </BaseModal>
    );
});

SubmitModal.displayName = 'SubmitModal';
